/**
 * Protocol utilities - uses BD SDK for protocol metadata
 */

import { queryClient } from "@blockdaemon/widget-host";

import { fetchProtocolMetadata } from "./sdk-protocol-metadata";

export enum ProtocolKey {
  AAVE_V2 = "aave-v2",
  AAVE_V3 = "aave-v3",
  AAVE_V3_TESTNET = "aave-v3-testnet",
  COMPOUND_V2 = "compound-v2",
  COMPOUND_V3 = "compound-v3",
  MORPHO = "morpho",
}
export interface LendBorrowProject {
  id: string;
  key: ProtocolKey;
  name: string;
  chainId: string;
  logoUrl: string;
  isTestnet?: boolean;
}

// Query key factory for protocol data
const protocolQueryKeys = {
  all: ["protocols"] as const,
  lists: () => [...protocolQueryKeys.all, "list"] as const,
};

const protocolQueryConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  refetchInterval: 10 * 60 * 1000, // 10 minutes
};

/**
 * Prefetch protocol data into the query cache.
 * Used by defi and portfolio route loaders.
 */
export async function prefetchProtocolData(): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: protocolQueryKeys.lists(),
    queryFn: fetchProtocols,
    ...protocolQueryConfig,
  });
}

async function fetchProtocols(): Promise<{
  items: Array<{
    sys: { id: string; createdAt: string; updatedAt: string };
    fields: Record<string, unknown>;
  }>;
}> {
  try {
    // Use unified utility to fetch protocol metadata
    const protocolData = await fetchProtocolMetadata();

    // Transform protocol metadata to expected format
    const items: Array<{
      sys: { id: string; createdAt: string; updatedAt: string };
      fields: Record<string, unknown>;
    }> = [];

    for (const item of protocolData) {
      const protocolName = item.protocolName || "";
      const protocolLogo = item.protocolLogo || "";
      const protocolKey = protocolName.toLowerCase().replace(/\s+/g, "-");

      for (const chain of item.supportedChains) {
        items.push({
          sys: {
            id: chain.protocolId || "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          fields: {
            name: protocolName,
            symbol: protocolKey,
            description: `${protocolName} protocol on ${chain.chainName || ""}`,
            logo: {
              fields: {
                file: {
                  url: protocolLogo,
                },
                title: `${protocolName} Logo`,
                description: `Logo for ${protocolName}`,
              },
            },
          },
        });
      }
    }

    return { items };
  } catch (error) {
    console.error("Error fetching protocols from BD SDK:", error);
    return { items: [] };
  }
}
